import { useCallback, useEffect, useState } from 'react';
import { CopilotAnswerService, useMountSendMessage } from '../..';
import { useSelector } from 'react-redux';

export function useCopilotAnswer() {
  // store - redux
  const currentChat = useSelector((state: any) =>
    state?.chat?.chats?.find(
      (chat: any) => chat.id === state.chat.chatSelectedId
    )
  );

  const currentMessages = useSelector(
    (state: any) =>
      state?.chat?.chats?.find(
        (chat: any) => chat.id === state.chat.chatSelectedId
      )?.messages
  );

  const user_id = useSelector((state: any) => state.chat?.config?.user?.id);

  const [isOpenByDefault, setIsOpenByDefault] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { handleMountSendMessage } = useMountSendMessage();

  const handleSendAnswer = useCallback(() => {
    setInput('');
    setIsOpen(false);
    handleMountSendMessage(input, currentChat);
  }, [currentChat?.id, input]);

  const handleCreateCopilotAnswer = useCallback(
    async (lastMessages: any[], contactId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await CopilotAnswerService.createCopilotAnswer(
          lastMessages,
          contactId
        );
        setInput(response.suggestion);
        setIsLoading(false);
      } catch (error) {
        setError(error as string);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const retryRequest = useCallback(() => {
    handleCreateCopilotAnswer(currentMessages.slice(-20), currentChat?.id);
  }, [currentMessages, currentChat?.id]);

  const handleUpdateContactCopilotAnswer = useCallback(
    async (suggest_answer: boolean) => {
      try {
        setIsLoading(true);
        await CopilotAnswerService.updateContactCopilot(
          suggest_answer,
          user_id
        );
        setIsOpenByDefault((prev) => !prev);
        setIsLoading(false);
      } catch (error) {
        console.error('error update contact copilot', error);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const handleGetContactCopilotAnswer = useCallback(async (user_id: string) => {
    try {
      setIsLoading(true);
      const response = await CopilotAnswerService.getContactCopilot(user_id);
      setIsOpenByDefault(response.suggest_answer);

      if (response.suggest_answer) {
        openOrCloseByLastMessage();
      } else {
        setIsOpen(false);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('error get contact copilot', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reset quando muda de chat
  useEffect(() => {
    if (currentChat?.id) {
      setInput('');
    }
  }, [currentChat?.id]);

  useEffect(() => {
    if (!user_id) {
      return;
    }

    handleGetContactCopilotAnswer(user_id);
  }, [user_id]);

  useEffect(() => {
    if (!isOpenByDefault) {
      return;
    }

    openOrCloseByLastMessage();
  }, [currentMessages]);

  const openOrCloseByLastMessage = () => {
    if (currentChat?.id && currentMessages?.length > 0) {
      const lastMessage = currentMessages[currentMessages.length - 1];

      // Só sugerir se a última mensagem for receptiva (dir === 'i')
      if (lastMessage.dir !== 'i') {
        setIsOpen(false);
      } else {
        setIsOpen(true);
        handleCreateCopilotAnswer(currentMessages.slice(-20), currentChat?.id);
      }
    } else {
      setIsOpen(false);
    }
  };

  // Handle do click no botão para abrir Copilot
  const handleOpen = () => {
    if (!currentChat?.id && currentMessages?.length === 0) {
      return;
    }

    const lastMessage = currentMessages[currentMessages.length - 1];

    // Só sugerir se a última mensagem for receptiva (dir === 'i')
    if (lastMessage.dir === 'i') {
      setIsOpen(true);
      handleUpdateContactCopilotAnswer(true);
      handleCreateCopilotAnswer(currentMessages.slice(-20), currentChat?.id);
    }
  };

  // Handle do click no botão para fechar Copilot
  const handleClose = () => {
    setIsOpen(false);
    handleUpdateContactCopilotAnswer(false);
  };

  return {
    isOpen,
    input,
    isOpenByDefault,
    setIsOpen, // Usar handleClose em vez de setIsOpen diretamente
    setInput,
    handleSendAnswer,
    handleUpdateContactCopilotAnswer,
    handleOpen,
    handleClose,
    isLoading,
    error,
    retryRequest,
  };
}
